<template>
<div>
    <div class="branch-line">
        <div class="header">
            <h3 v-text="strings.heading"></h3>
            <div class="actions">
                <i v-if="can.clone" @click="cloneBranch" class="fa fa-files-o" aria-hidden="true"></i>
                <i v-if="can.delete" @click="removeBranch" class="fa fa-trash" aria-hidden="true"></i>
            </div>
        </div>
        <div v-if="main && mainSegmentUid">
            <div v-if="isBidirectional">
                <lat-long-segment
                    :uid="mainSegmentUid"
                    :lateralLimit="lateralLimit"
                    :longitudinalLimit="longitudinalLimit"
                    :mainBranch="true">
                </lat-long-segment>
            </div>
            <div v-else>
                <line-segment
                    :uid="mainSegmentUid"
                    :lateralLimit="lateralLimit"
                    :longitudinalLimit="longitudinalLimit"
                    :mainBranch="true">
                </line-segment>
            </div>
            <div class="header">
                <h3>Segments</h3>
            </div>
            <div v-if="isBidirectional">
                <lat-long-segment
                    v-for="uid in supplementarySegmentUids"
                    :key="uid"
                    :uid="uid"
                    :lateralLimit="lateralLimit"
                    :longitudinalLimit="longitudinalLimit"
                    :mainBranch="true">
                </lat-long-segment>
            </div>
            <div v-else>
                <line-segment
                    v-for="uid in supplementarySegmentUids"
                    :key="uid"
                    :uid="uid"
                    :lateralLimit="lateralLimit"
                    :longitudinalLimit="longitudinalLimit"
                    :mainBranch="true">
                </line-segment>
            </div>
        </div>
        <div v-else>
            <line-segment
                v-for="uid in segmentUids"
                :key="uid"
                :uid="uid"
                :riserNippleCalculation="record.riserNippleCalculation">
            </line-segment>
        </div>

        <!-- @update:data="val => segment = val" -->

        <p class="messaging error simple" v-if="main && mainBranchLengthErrors.length" v-for="error in mainBranchLengthErrors" v-text="error"></p>
        <p>
            <span v-show="maxLoadError" class="messaging error simple" v-text="maxLoadError"></span>
        </p>

        <span class="add-segment" @click="addSegment()">
            <i class="fa fa-plus"></i>
            Add a Segment
        </span>
        <span class="add-segment" @click="addRiserNipple" v-if="!main && !riserNippleUid">
            <i class="fa fa-plus"></i>
            Add a Riser Nipple
        </span>
    </div>
    <div class="total subtotal">
        <fieldset v-show="!main">
            <div class="text">
                <label v-text="strings.copies"></label>
                <input type="text" v-model="localData.copies">
            </div>
        </fieldset>
        <fieldset v-show="!main">
            <div class="text">
                <label for="total" v-text="strings.weight"></label>
                <span v-if="record.weight" v-text="lbOrKg(record.weight, true, false, 2)"></span>
                <span v-else>--</span>
            </div>
        </fieldset>
        <fieldset v-show="main">
            <div class="text" v-show="maxLoadLimit">
                <label>Allowable Load on Main</label>
                <span v-text="maxLoadLimit"></span>
            </div>
        </fieldset>
        <fieldset>
            <div class="text">
                <label for="total" v-text="strings.totalWeight"></label>
                <span v-if="record.totalWeight" v-text="lbOrKg(record.totalWeight, true, false, 2)"></span>
                <span v-else>--</span>
            </div>
        </fieldset>        
    </div>
</div>
</template>

<script>
import LineSegment from './line-segment';
import LatLongSegment from './lat-long-segment';

import { mapKeyedGetters } from '../../../common/vuex-extra';
import { mapGetters } from 'vuex';

export default {
    name: 'seismic-branch-line',
    components: {
        LineSegment,
        LatLongSegment
    },
    props: [
        'uid',
        'displayBranch',
        'lateralLimit',
        'longitudinalLimit',
    ],
    data() {
        return {
            localData: {}
        };
    },
    mounted() {
        this.localData = Object.assign({}, this.record);
    },
    watch: {
        localData: {
            handler() {
                this.$store.dispatch('submittalSystem/setBranch', this.localData);
            },
            deep: true
        },
        displayBranch: {
            handler(){
                this.switchDisplayBranch();
            } 
        }
    },
    computed: {
        ...mapKeyedGetters({
            'index': 'submittalSystem/branchIndex',
            'record': 'submittalSystem/branch',
            'mainSegmentUid': 'submittalSystem/mainSegmentUid',
            'normalSegmentUids': 'submittalSystem/normalSegmentUids',
            'segmentUids': 'submittalSystem/segmentUids',
            'supplementarySegmentUids': 'submittalSystem/supplementarySegmentUids',
            'riserNippleUid': 'submittalSystem/riserNippleUid'
        }),
        ...mapGetters({
            "mainBranchLengthErrors": "submittalSystem/mainBranchLengthErrors",
            'maxFpw' : 'submittalSystem/maxFpw',
            'orientation' : 'submittalSystem/orientation',
            'systemErrors' : 'submittalSystem/systemErrors'
        }),
        main() {
            return this.index === 0;
        },
        strings() {
            return {
                heading: `Define ${this.name}`,
                copies: `# of ${this.name}'s`,
                weight: `${this.name} Weight`,
                totalWeight: `${this.name} Total Weight`
            };
        },
        can() {
            return {
                clone: !this.main,
                delete: !this.main
            }
        },
        name() {
            if (this.index === 0) {
                return 'Main Line'
            } else {
                return `Branch Line ${this.index}`
            }
        },
        maxLoadError() {
            if (this.main && this.systemErrors.maxLoad) {
                return "Maximum Load (Fpw) in Zone of Influence exceeds the allowable lateral load in the Main";
            }
            return '';
        },
        maxLoadLimit() {
            if (!this.maxFpw || this.maxFpw == 'null') {
                return '';
            }

            return this.lbfOrKn(this.maxFpw, true, this.useMetric(2));
        },
        isBidirectional() {
            return ['lateral-longitudinal', 'longitudinal-lateral'].includes(this.orientation);
        },
    },
    methods: {
        addSegment() {
            this.$store.dispatch('submittalSystem/addSegment', {
                uid: this.localData.uid,
            });
        },
        addRiserNipple() {
            this.$store.dispatch('submittalSystem/addSegment', {
                uid: this.localData.uid,
                data: { riserNipple: true }
            });
        },
        cloneBranch() {
            this.$store.dispatch('submittalSystem/cloneBranch', this.localData);
        },
        removeBranch() {
            this.$store.dispatch('submittalSystem/removeBranch', this.localData);
        },
        switchDisplayBranch() {
            if (!this.displayBranch && this.can.delete) {
                this.removeBranch();
            }
        }
    }
}
</script>
